import {useCallback, useEffect, useMemo, useState} from "react";
import {Circle} from "@react-google-maps/api";
import {
  InfoWindowEntity,
  Map,
  MAP_DEFAULT_BOUNDS,
  MAP_DEFAULT_CENTER,
  MapProps,
  MapState,
  useMapLoader
} from "../Map/Map";
import {
  DEFAULT_LOCATION_HISTORY_MAP_FILTER,
  LocationHistoryMapFilter,
  ShiftLocationHistory,
  TransportRoute
} from "../../../model/location-history";
import {ResizeableContent} from "../../ResizableContent/ResizableContent";
import {
  isDisposalType,
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  DriverLocationEntity,
  firstOrUndefined,
  GeofenceEventEntity,
  isDriverLocation,
  isGeofenceEvent,
  isSite,
  isTransport,
  LocatableEntity,
  ShiftTimelineEvent,
  SiteEntity,
  siteType,
  TransportEntity,
  TransportShiftTimelineEvent,
  useGetAllSitesQuery
} from "@ots-frontend/ots-data";
import styled from "@emotion/styled";
import {extractGeofenceEvents, extractTransports, getLocationAtDttm, getShiftTimeline} from "./util";
import {SiteInfoWindow} from "../info-window/SiteInfoWindow/SiteInfoWindow";
import {GeofenceEventInfoWindow} from "../info-window/GeofenceEventInfoWindow/GeofenceEventInfoWindow";
import {DriverLocationInfoWindow} from "../info-window/DriverLocationInfoWindow/DriverLocationInfoWindow";
import {TransportInfoWindow} from "../info-window/TransportInfoWindow/TransportInfoWindow";
import {BaseShiftTimeline} from "../../timeline/ShiftTimeline/BaseShiftTimeline";
import {IncrementalDateInput} from "../../date/IncrementalDateInput/IncrementalDateInput";
import {TransportRoutePolylineMemo} from "../polyline/TransportRoutePolyline/TransportRoutePolyline";
import {Divider, Group, ScrollArea, Switch} from "@mantine/core";
import {Card} from "../../card/Card/Card";
import {TransportMarkers} from "./TransportMarkers/TransportMarkers";
import {GeofenceEventMarkers} from "./GeofenceEventMarkers/GeofenceEventMarkers";
import {skipToken} from "@reduxjs/toolkit/query";
import {locationHistoryMapSiteSelector} from "../../../store/ui/location-history-map-page/selectors";
import {useGetRouteGroupColorCode} from "../../../hooks/useGetRouteGroupColorCode";
import {SiteMarkerClustererMemo} from "../marker/SiteMarkerCluster/SiteMarkerCluster";
import {useApp} from "../../../providers/RegionProvider/RegionProvider"
import {Marker} from "../marker/Marker/Marker";
import {DriverLocationMarkers} from "./DriverLocationMarkers/DriverLocationMarkers";
import {Empty} from "../../empty/Empty/Empty";
import {ShiftRoutePolylineMemo} from "../polyline/ShiftRoutePolyline/ShiftRoutePolyline";
import {useNavigate} from "react-router-dom";
import {MapContextMenu, MapContextMenuContext, MapContextMenuItem} from "../MapContextMenu/MapContextMenu";
import {distance, point} from "@turf/turf";
import {HasAccess} from "../../auth/HasAccess/HasAccess";

export type LocationHistoryMapProps = {
  filter?: LocationHistoryMapFilter
  mapState?: MapState
  data?: ShiftLocationHistory
  onMapUnmount?: Pick<MapProps, 'onUnmount'>
}

const TimelinePanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
`;

const IncrementerContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  left: 0;
`;

const DEFAULT_MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeId: "satellite",
  disableDefaultUI: true,
  mapTypeControl: false,
  streetViewControl: false
};

type LocationAtTime = {
  latLng: google.maps.LatLngLiteral;
  dttm: number;
};
export const LocationHistoryMap = (props: LocationHistoryMapProps) => {
  const {
    filter,
    mapState,
    data,
    onMapUnmount,
  } = props;

  const navigate = useNavigate()

  const [map, setMap] = useState<google.maps.Map | undefined>();
  const [mapZoom, setMapZoom] = useState<number>(8);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(MAP_DEFAULT_CENTER);

  const [contextMenu, setContextMenu] = useState<MapContextMenuContext | undefined>(undefined);
  const [popupEntity, setPopupEntity] = useState<InfoWindowEntity<LocatableEntity> | undefined>(undefined);
  const [hoverPopupEntity, setHoverPopupEntity] = useState<InfoWindowEntity<LocatableEntity> | undefined>(undefined);

  const [geofenceSites, setGeofenceSites] = useState<SiteEntity[]>([]);
  const [visibleGeofenceEvents, setVisibleGeofenceEvents] = useState<GeofenceEventEntity[]>([]);
  const [visibleTransports, setVisibleTransports] = useState<TransportEntity[]>([]);
  const [visibleTransportRoutes, setVisibleTransportRoutes] = useState<TransportRoute[]>([]);
  const [visibleDriverLocations, setVisibleDriverLocations] = useState<DriverLocationEntity[]>([]);

  const [mapDttm, setMapDttm] = useState<number | undefined>()
  const [mapDttmLocation, setMapDttmLocation] = useState<LocationAtTime | undefined>(undefined)

  const [internalFilter, setInternalFilter] = useState<LocationHistoryMapFilter>(filter ?? DEFAULT_LOCATION_HISTORY_MAP_FILTER)

  const {isLoaded: isMapLoaded} = useMapLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  const {regionId} = useApp()

  const selectSites = useMemo(() => {
    return locationHistoryMapSiteSelector();
  }, []);

  const {sites} = useGetAllSitesQuery(regionId ?? skipToken, {
    selectFromResult: (result) => {
      return {
        sites: selectSites(result, {
          showSites: internalFilter.showSites,
          bbox: data?.bbox ?? [0, 0, 0, 0]
        }),
      }
    }
  });

  const {routeGroupColorCode} = useGetRouteGroupColorCode({regionId: regionId})

  const _onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);

    if (mapState?.bounds) {
      map.fitBounds(mapState.bounds);
    } else if (data?.bbox) {
      const bounds = new google.maps.LatLngBounds(
        {
          lat: data.bbox[0],
          lng: data.bbox[1]
        },
        {
          lat: data.bbox[2],
          lng: data.bbox[3]
        }
      )
      map.fitBounds(bounds);
    } else {
      map.fitBounds(MAP_DEFAULT_BOUNDS);
    }

  }, [data?.bbox, mapState?.bounds]);

  const _onMapUnmount = useCallback((map: google.maps.Map) => {
    setMap(undefined);

    if (onMapUnmount) {
      // @ts-ignore
      onMapUnmount(map);
    }
  }, [onMapUnmount]);

  useEffect(() => {
    if (map) {
      if (data?.bbox) {
        const bounds = new google.maps.LatLngBounds(
          {
            lat: data.bbox[0],
            lng: data.bbox[1]
          },
          {
            lat: data.bbox[2],
            lng: data.bbox[3]
          }
        )
        map.fitBounds(bounds);
      }
    }

    // setMapDttm(data?.minDttm)
  }, [map, data?.bbox, data?.minDttm])

  const onMapZoomChanged = useCallback(() => {
    const zoom = map?.getZoom();
    if (zoom) {
      if (zoom < 15) {
        setGeofenceSites([]);
        setVisibleDriverLocations([])
      } else {
        const newGeofenceSites: SiteEntity[] = [];
        const newDriverLocations: DriverLocationEntity[] = []

        const bounds = map?.getBounds();
        if (bounds) {

          for (const site of sites) {
            if (site.location && site.geofenceExtension) {
              const latLng: google.maps.LatLngLiteral = {
                lat: site.location.latCoord,
                lng: site.location.longCoord
              };
              if (bounds.contains(latLng)) {
                newGeofenceSites.push(site);
              }
            }
          }

          if (!internalFilter || internalFilter.showDriverLocations) {
            const driverLocations: DriverLocationEntity[] = []

            if (data.transportRoutes.length > 0) {
              for (const transportRoute of visibleTransportRoutes) {
                driverLocations.push(...transportRoute.driverLocations)
              }
            } else {
              driverLocations.push(...data.driverLocations)
            }

            for (const dl of driverLocations) {
              const latLng: google.maps.LatLngLiteral = {
                lat: dl.latCoord,
                lng: dl.longCoord
              };
              if (bounds.contains(latLng)) {
                newDriverLocations.push(dl);
              }
            }
          }

        }

        setGeofenceSites(newGeofenceSites);
        setVisibleDriverLocations(newDriverLocations)
      }
    }
  }, [data?.driverLocations, data?.transportRoutes?.length, internalFilter, map, sites, visibleTransportRoutes])

  const onMapRightClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.domEvent && event.domEvent instanceof MouseEvent) {
      const mouseEvent = event.domEvent as MouseEvent;
      if (map) {
        const mapDiv = map.getDiv()
        if (mapDiv) {
          const x = mouseEvent.clientX;
          const y = mouseEvent.clientY;

          const width = 1;
          const height = 1;

          setContextMenu({
            event: event,
            bounds: {
              left: x,
              top: y,
              right: x + width,
              bottom: y + height,
              width,
              height
            }
          })
        }
      }
    }
  }, [map])

  const getRelevantTransport = useCallback((event: google.maps.MapMouseEvent) => {
    let minDistance = Infinity;
    let closestTransport: TransportEntity | undefined = undefined

    const root = point([event.latLng.lng(), event.latLng.lat()])

    for (const transport of visibleTransports) {
      if (transport.coordinates) {
        const d = distance(root, point([transport.coordinates.longCoord, transport.coordinates.latCoord]), {units: 'miles'});
        if (d < minDistance) {
          minDistance = d;
          closestTransport = transport;
        }
      }
    }

    return closestTransport
  }, [visibleTransports])

  const onMapContextItemClick = useCallback((item: MapContextMenuItem, event: google.maps.MapMouseEvent) => {
    if (item === "NEW_GEOFENCE") {

      const relevantTransport = getRelevantTransport(event)

      navigate("/geofences/form", {
        state: {
          id: undefined,
          formDefaults: {
            latCoord: event.latLng.lat(),
            longCoord: event.latLng.lng(),
            object: relevantTransport ? {
              id: relevantTransport.siteId,
              type: 'SITE',
            } : undefined
          }
        }
      });
    }
  }, [navigate, getRelevantTransport])

  useEffect(() => {
    onMapZoomChanged();
  }, [onMapZoomChanged, map, sites]);

  useEffect(() => {
    if ((!internalFilter || internalFilter.showGeofenceEvents) && data) {
      setVisibleGeofenceEvents(extractGeofenceEvents(data?.events));
    } else {
      setVisibleGeofenceEvents([]);
    }
  }, [internalFilter?.showGeofenceEvents, data, internalFilter]);

  useEffect(() => {
    if (data) {
      setVisibleTransports(
        extractTransports(data?.events, internalFilter?.selectedTransportIds)
      );
    } else {
      setVisibleTransports([]);
    }
  }, [internalFilter?.selectedTransportIds, data]);

  useEffect(() => {
    if (data) {
      const selectedTransportIds = internalFilter?.selectedTransportIds
      if (selectedTransportIds) {
        const routes = data.transportRoutes.filter((r) =>
          selectedTransportIds.findIndex((id) => id === r.transport.id) !== -1
        );
        setVisibleTransportRoutes(routes);
      } else {
        setVisibleTransportRoutes(data.transportRoutes)
      }
    } else {
      setVisibleTransportRoutes([]);
    }
  }, [internalFilter?.showTransportRoutes, internalFilter?.selectedTransportIds, data]);

  const onHover = useCallback(
    (event: google.maps.MapMouseEvent, hoverEntity: LocatableEntity) => {
      if (event.latLng) {
        setHoverPopupEntity({entity: hoverEntity, latLng: event.latLng});
      } else {
        setHoverPopupEntity(undefined);
      }
    },
    []
  );

  const onHoverOut = useCallback((_event: google.maps.MapMouseEvent) => {
    setHoverPopupEntity(undefined);
  }, []);

  const onMarkerClick = useCallback(
    (event: google.maps.MapMouseEvent, markerEntity: LocatableEntity) => {
      if (event.latLng) {
        setPopupEntity({entity: markerEntity, latLng: event.latLng});
      } else {
        setPopupEntity(undefined);
      }
      onHover(event, markerEntity);
    },
    [onHover]
  );

  const clearPopup = useCallback(() => {
    setPopupEntity(undefined)
  }, [])

  const renderEntityPopup = useCallback(
    (popupEntity: InfoWindowEntity<LocatableEntity> | undefined) => {
      if (popupEntity) {
        const entity = popupEntity.entity;
        if (isSite(entity)) {
          return (
            <SiteInfoWindow
              entity={entity}
              position={popupEntity.latLng}
              onCloseClick={clearPopup}
            />
          );
        } else if (isGeofenceEvent(entity)) {
          return (
            <GeofenceEventInfoWindow
              entity={entity}
              position={popupEntity.latLng}
              onCloseClick={clearPopup}
            />
          );
        } else if (isDriverLocation(entity)) {
          return (
            <DriverLocationInfoWindow
              entity={entity}
              position={popupEntity.latLng}
              onCloseClick={clearPopup}
            />
          );
        } else if (isTransport(entity)) {
          return (
            <TransportInfoWindow
              entity={entity}
              position={popupEntity.latLng}
              onCloseClick={clearPopup}
            />
          );
        }
      }
      return undefined;
    },
    [clearPopup]
  );

  const renderHoverEntityPopup = useCallback(() => {
    return renderEntityPopup(hoverPopupEntity);
  }, [renderEntityPopup, hoverPopupEntity]);

  const renderMarkerEntityPopup = useCallback(() => {
    return renderEntityPopup(popupEntity);
  }, [renderEntityPopup, popupEntity]);

  const markTimeOnMap = useCallback(
    (time: Date | undefined, focus: boolean) => {
      if (map) {
        if (time && data) {
          const coordinatesAtTime = getLocationAtDttm(time, data);

          if (coordinatesAtTime) {
            const latLng = {
              lat: coordinatesAtTime.latCoord,
              lng: coordinatesAtTime.longCoord,
            };
            if (focus) {
              map.panTo(latLng);
              map.setZoom(15)
            }
            setMapDttmLocation({
              latLng: latLng,
              dttm: time.getTime(),
            });
          }
        } else {
          setMapDttmLocation(undefined);
        }
      }
    },
    [map, data]
  );

  useEffect(() => {
    if (mapDttm) {
      markTimeOnMap(adjustToTimeZone(mapDttm), true)
    }
  }, [mapDttm, markTimeOnMap])

  const onTimelineItemClick = useCallback(
    (event: ShiftTimelineEvent) => {
      if (map) {
        const coordinatesAtTime = getLocationAtDttm(event.date, data);
        if (coordinatesAtTime) {
          map.panTo({
            lat: coordinatesAtTime.latCoord,
            lng: coordinatesAtTime.longCoord,
          });
          map.setZoom(15)
        }
      }
    },
    [map, data]
  );

  const onTransportChecked = useCallback(
    (event: TransportShiftTimelineEvent, value: boolean) => {
      setInternalFilter((prevState) => {
        const transportIds: string[] = []
        if (prevState?.selectedTransportIds) {
          if (value) {
            transportIds.push(...prevState.selectedTransportIds)
            transportIds.push(event.id)
          } else {
            for (const transportId of prevState.selectedTransportIds) {
              if (transportId !== event.id) {
                transportIds.push(transportId)
              }
            }
          }
        } else if (value) {
          transportIds.push(event.id)
        }

        return {
          ...prevState ?? DEFAULT_LOCATION_HISTORY_MAP_FILTER,
          selectedTransportIds: transportIds
        }
      })
    },
    []
  );

  const getSiteIconColor = useCallback((site: SiteEntity) => {
    let color: string | undefined = undefined
    if (routeGroupColorCode) {
      let key: string
      const route = firstOrUndefined(site.routes)
      if (route?.routeGroupId) {
        key = route?.routeGroupId
      } else {
        if (isDisposalType(site.type)) {
          key = 'undefined-disposal';
        } else {
          key = 'undefined';
        }
      }

      const colorCode = routeGroupColorCode[key];
      color = colorCode?.color
    }

    return color
  }, [routeGroupColorCode])

  const onDateChange = useCallback((date: Date) => {
    setMapDttm(date.getTime())
  }, [])

  const toggleDriverLocations = useCallback((value: boolean) => {
    setInternalFilter((prevState) => ({
      ...prevState,
      showDriverLocations: value
    }))
  }, [])

  const toggleSites = useCallback((value: boolean) => {
    setInternalFilter((prevState) => ({
      ...prevState,
      showSites: value
    }))
  }, [])

  const shiftTimeline = useMemo(() => {
    return data ? getShiftTimeline(data, internalFilter?.selectedTransportIds) : undefined
  }, [data, internalFilter.selectedTransportIds])

  if (!isMapLoaded) {
    return <div/>;
  }

  return (
    <ResizeableContent>
      {(size) => (
        <Map
          onLoad={_onMapLoad}
          onUnmount={_onMapUnmount}
          onZoomChanged={onMapZoomChanged}
          onBoundsChanged={onMapZoomChanged}
          zoom={mapZoom}
          center={mapCenter}
          mapContainerStyle={{width: size.width ?? "100%", height: size.height ?? "100%"}}
          options={DEFAULT_MAP_OPTIONS}
          onRightClick={onMapRightClick}
        >
          {renderHoverEntityPopup()}
          {renderMarkerEntityPopup()}

          {visibleTransportRoutes.map((route, index) => {
            return <TransportRoutePolylineMemo key={'route_' + index + "_" + route.transport.id} route={route}/>;
          })}

          {typeof data !== 'undefined' && data.transportRoutes.length === 0 &&
            <ShiftRoutePolylineMemo id={data.shift.id} route={data.driverLocations}/>
          }

          <TransportMarkers
            transports={visibleTransports}
            onHover={onHover}
            onHoverOut={onHoverOut}
            onMarkerClick={onMarkerClick}
          />

          <GeofenceEventMarkers
            geofenceEvents={visibleGeofenceEvents}
            onHover={onHover}
            onHoverOut={onHoverOut}
            onMarkerClick={onMarkerClick}
          />

          <DriverLocationMarkers
            driverLocations={visibleDriverLocations}
            onHover={onHover}
            onHoverOut={onHoverOut}
            onMarkerClick={onMarkerClick}
          />

          <SiteMarkerClustererMemo
            entities={sites}
            onHover={onHover}
            onHoverOut={onHoverOut}
            onMarkerClick={onMarkerClick}
            iconColor={getSiteIconColor}
          />

          {geofenceSites.map((s) => {
            return (
              <Circle
                key={s.id + "-geofence"}
                center={{lat: s.latCoord!, lng: s.longCoord!}}
                radius={s.geofenceExtension!}
              />
            );
          })}

          {mapDttmLocation &&
            <Marker
              position={mapDttmLocation.latLng}
              animation={google.maps.Animation.BOUNCE}
            />
          }

          <TimelinePanelContainer>
            <ScrollArea h="100%" type="hover" scrollHideDelay={100}>
              <Card
                mih={150}
                w={300}
                mah="100%"
                shadow={"sm"}
                padding={"lg"}
                radius={"md"}
                withBorder={true}>
                {shiftTimeline ? (
                  <BaseShiftTimeline
                    timeline={shiftTimeline}
                    onTimelineItemClick={onTimelineItemClick}
                    onTransportChecked={onTransportChecked}
                  />
                ) : (
                  <Empty message={"No Location Data"}/>
                )}
              </Card>
            </ScrollArea>
          </TimelinePanelContainer>

          {data &&
            <IncrementerContainer>
              <Card
                padding="xs"
              >
                <IncrementalDateInput
                  valueFormat="MM/DD/YYYY HH:mm"
                  defaultValue={data.minDttm ? adjustFromTimeZoneSafely(data.minDttm) : undefined}
                  showIncrement={true}
                  minDate={data.minDttm ? adjustFromTimeZoneSafely(data.minDttm) : undefined}
                  maxDate={data.maxDttm ? adjustFromTimeZoneSafely(data.maxDttm) : undefined}
                  onChange={onDateChange}
                  popoverProps={{
                    withinPortal: true
                  }}
                />
                <Divider variant="dotted" my="sm"/>
                <Group p="sm">
                  <Switch
                    label={"Show GPS"}
                    checked={internalFilter.showDriverLocations}
                    onChange={(e) => toggleDriverLocations(e.currentTarget.checked)}/>
                  <Switch
                    label={"Show Sites"}
                    checked={internalFilter.showSites}
                    onChange={(e) => toggleSites(e.currentTarget.checked)}/>
                </Group>
              </Card>

            </IncrementerContainer>
          }

          <HasAccess permissions={["geofence_create"]}>
            <MapContextMenu
              context={contextMenu}
              onItemClick={onMapContextItemClick}
              onOutsideClick={() => setContextMenu(undefined)}/>
          </HasAccess>

        </Map>
      )}
    </ResizeableContent>
  );
};

